/**
 * Interstitial page shown after the last questionnaire question: a recap of
 * the user's answers (age, height/weight/BMI, goal weight, current
 * treatment, diagnosed conditions, other health factors) before continuing
 * to treatment matching.
 *
 * Desktop and mobile are rendered as separate blocks (not shared classes
 * with lg: overrides), matching WeightLossProjectionPage/RealResultsPage.
 *
 * HL-12029 (arm B, 100% since HL-12595) — Figma 5713:20026: "Ihr Gesundheitsprofil"
 * chips first, then the "Auswertungen" rows (each with a pencil that re-opens the
 * question), then the doctor note card; no white card around the blocks. The "Ihr Ziel"
 * pencil opens the health profile modal, since the goal is derived from the current weight.
 */

import React from "react";
import GoalScale from "../../../icons/GoalScale.png";
import EditPencil from "../../../icons/EditPencil.svg";
import * as translator from "../../../utils/translator";
import { formatDecimal } from "../../../utils/decimalInput";
import { HealthSummaryEntry } from "./healthSummaryHelpers";
import { formatFtIn, formatStLb, isImperialHeight, isImperialWeight } from "./units";
import { useShellId, useShellIdFn } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

const ICON_COLOR = "#f59200";

const GoalIcon: React.FC = () => <img src={GoalScale} alt="" className="size-4" />;

const AssessmentIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="2.5" width="10" height="12" rx="1.5" stroke={ICON_COLOR} strokeWidth="1.2" />
    <path d="M6 1.5h4a1 1 0 011 1v1H5v-1a1 1 0 011-1z" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5.5 8h5M5.5 10.5h5M5.5 5.5h2" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const CheckBadge: React.FC = () => (
  <div className="shrink-0 bg-[#3aa169] rounded-full p-[6px] flex items-center justify-center">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7.2l2.6 2.6L11 4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

/** Section slugs for `summary_edit_clicked`; question rows use the analytics `q{id}` convention. */
export const SUMMARY_EDIT_SECTION_GOAL = "goal";
export const summaryEditSectionForQuestion = (questionId: number | string): string => `q${questionId}`;

interface SummaryRow {
  key: string;
  Icon: React.FC;
  label: string;
  value: string;
  /** What the pencil does. Undefined = no pencil. */
  onEdit?: () => void;
  editId?: string;
}

interface HealthProfileSummaryPageProps {
  language?: string;
  ageYears: number | null;
  heightCm: number | null;
  weightKg: number | null;
  bmi: number | null;
  goalWeightKg: number | null;
  healthSummaryEntries: HealthSummaryEntry[];
  /** "Ihr Ziel" pencil → health profile modal. */
  onEditGoal?: () => void;
  /** Answer row pencil → that question's pop-up. */
  onEditEntry?: (entry: HealthSummaryEntry) => void;
}

const HealthProfileSummaryPage: React.FC<HealthProfileSummaryPageProps> = ({
  language,
  ageYears,
  heightCm,
  weightKg,
  bmi,
  goalWeightKg,
  healthSummaryEntries,
  onEditGoal,
  onEditEntry,
}) => {
  // QuestionFrame renders this body in both shells; scope every id per shell.
  const scopeId = useShellIdFn();
  const editableRootId = useShellId("wl2_health_summary_editable");

  const badges = [
    ageYears != null ? { key: "age", text: t(language, "health_summary_age_badge").replace("{age}", String(ageYears)) } : null,
    heightCm != null
      ? { key: "height", text: isImperialHeight() ? formatFtIn(heightCm) : t(language, "health_summary_height_badge").replace("{height}", formatDecimal(heightCm, language)) }
      : null,
    weightKg != null
      ? { key: "weight", text: isImperialWeight() ? formatStLb(weightKg) : t(language, "health_summary_weight_badge").replace("{weight}", formatDecimal(weightKg, language)) }
      : null,
    bmi != null ? { key: "bmi", text: t(language, "health_summary_bmi_badge").replace("{bmi}", bmi.toLocaleString(language || "de", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) } : null,
  ].filter((b): b is { key: string; text: string } => b !== null);

  const goalRow: SummaryRow | null =
    goalWeightKg != null
      ? {
          key: "goal",
          Icon: GoalIcon,
          label: t(language, "health_summary_goal_label"),
          value: isImperialWeight()
            ? t(language, "health_summary_goal_value_imperial").replace("{weight}", formatStLb(goalWeightKg))
            : t(language, "health_summary_goal_value").replace("{weight}", String(goalWeightKg)),
          onEdit: onEditGoal,
          editId: "wl2_health_summary_edit_goal",
        }
      : null;

  const assessmentRows: SummaryRow[] = healthSummaryEntries.map((entry) => ({
    key: String(entry.key),
    Icon: AssessmentIcon,
    label: entry.label,
    value: entry.value,
    onEdit: onEditEntry ? () => onEditEntry(entry) : undefined,
    editId: `wl2_health_summary_edit_${summaryEditSectionForQuestion(entry.questionId)}`,
  }));

  const rows = [goalRow, ...assessmentRows].filter((r): r is SummaryRow => r !== null);

  const editAria = t(language, "health_summary_edit_aria");

  // Row: icon + label/value, pencil pinned to the right edge (Figma 7269:25725).
  // `size` only picks the desktop / mobile dimensions; the shell scopes the ids.
  const renderEditableRow = (r: SummaryRow, size: "desktop" | "mobile") => (
    <div key={r.key} className="flex items-start justify-between gap-4 min-w-0 w-full">
      <div className={`flex ${size === "desktop" ? "gap-[18px]" : "gap-4"} items-start min-w-0`}>
        <div
          className={`shrink-0 bg-[#f9f9f9] flex items-center justify-center mt-1 ${
            size === "desktop" ? "rounded-[16px] size-9" : "rounded-[14px] size-8"
          }`}
        >
          <r.Icon />
        </div>
        <div className="flex flex-col min-w-0">
          <p className={`text-[#434343] font-['Poppins'] ${size === "desktop" ? "text-[14px]" : "text-[12px]"}`}>{r.label}</p>
          <p
            className={`text-[#08080a] font-medium font-['Poppins'] break-words ${
              size === "desktop" ? "text-[16px]" : "text-[15px]"
            }`}
          >
            {r.value}
          </p>
        </div>
      </div>
      {r.onEdit && (
        <button
          id={r.editId ? scopeId(r.editId) : undefined}
          type="button"
          onClick={r.onEdit}
          aria-label={editAria}
          className="shrink-0 size-8 flex items-center justify-center cursor-pointer hover:opacity-70 active:scale-95 transition mt-1"
          style={{ border: "none", background: "transparent", padding: 0 }}
        >
          <img src={EditPencil} alt="" className="size-4" />
        </button>
      )}
    </div>
  );

  const renderBadges = (size: "desktop" | "mobile") => (
    <div className={`flex items-center flex-wrap ${size === "desktop" ? "gap-4" : "gap-[9px]"}`}>
      {badges.map((b) => (
        <div
          key={b.key}
          className={`bg-[#fafafa] border border-[#f5f5f5] flex items-center justify-center ${
            size === "desktop" ? "rounded-[12px] px-[15px] py-[8px]" : "rounded-[9px] px-[12px] py-[6px]"
          }`}
        >
          <p className={`text-[#171717] font-['Poppins'] whitespace-nowrap ${size === "desktop" ? "text-[16px]" : "text-[12px]"}`}>
            {b.text}
          </p>
        </div>
      ))}
    </div>
  );

  const footerNote = (size: "desktop" | "mobile") => (
    <div
      className={`bg-white rounded-[12px] shadow-[0px_1px_3px_0px_rgba(202,202,202,0.1),0px_4px_24px_0px_rgba(216,206,206,0.08)] flex items-center gap-3 py-4 ${
        size === "desktop" ? "px-5" : "px-4"
      }`}
    >
      <CheckBadge />
      <p className="text-[#525252] text-[12px] font-light font-['Poppins']">{t(language, "health_summary_footer_note")}</p>
    </div>
  );

  return (
    <div id={editableRootId} className="flex flex-col gap-6">
      {/* Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:gap-10">
        <h1 className="text-[28px] font-medium text-[#08080a] font-['Poppins'] leading-[1.3] text-center">
          {t(language, "health_summary_title")}
        </h1>
        <div className="flex flex-col gap-4">
          <p className="text-[#08080a] text-[20px] font-medium font-['Poppins']">
            {t(language, "health_summary_card_title")}
          </p>
          {renderBadges("desktop")}
        </div>
        <div className="flex flex-col gap-6">
          <p className="text-[#08080a] text-[20px] font-medium font-['Poppins']">
            {t(language, "health_summary_edit_assessments_title")}
          </p>
          <div className="grid grid-cols-2 gap-x-[18px] gap-y-[30px]">{rows.map((r) => renderEditableRow(r, "desktop"))}</div>
        </div>
        {footerNote("desktop")}
      </div>

      {/* Mobile — Figma 5713:20026: 40px between blocks, 24px heading→rows */}
      <div className="lg:hidden flex flex-col gap-10">
        <h1 className="text-[24px] font-medium text-[#08080a] font-['Poppins'] leading-[1.3]">
          {t(language, "health_summary_title")}
        </h1>
        <div className="flex flex-col gap-[14px]">
          <p className="text-[#08080a] text-[18px] font-medium font-['Poppins'] leading-[28px]">
            {t(language, "health_summary_card_title")}
          </p>
          {renderBadges("mobile")}
        </div>
        <div className="flex flex-col gap-6">
          <p className="text-[#08080a] text-[18px] font-medium font-['Poppins']">
            {t(language, "health_summary_edit_assessments_title")}
          </p>
          <div className="flex flex-col gap-4">{rows.map((r) => renderEditableRow(r, "mobile"))}</div>
        </div>
        {footerNote("mobile")}
      </div>
    </div>
  );
};

export default HealthProfileSummaryPage;
